import React from 'react';
import CtaSection from '../components/CtaSection';
import { ArchImage, Label, Mullions, Rail, Reveal, RuledRows, RuledSpread, Section, SectionHead } from '../components/System';
import useSeo from '../lib/useSeo';

const STORY_STEPS = [
  { title: 'First version', detail: 'Two tables. One job: explain a variance.' },
  { title: 'The hard part', detail: 'Stay exact on data it had never seen.' },
  { title: 'Today', detail: 'Connect a database or upload a file. Ask away.' },
];

const FACTS = [
  { value: '2026', label: 'Founded' },
  { value: 'Early', label: 'Stage' },
  { value: '4', label: 'Connectors' },
];

const PRINCIPLES = [
  { title: 'Exact numbers', detail: 'The database does the maths. The AI only explains.' },
  { title: 'Show the work', detail: 'Every answer shows its calculation, beside the summary.' },
  { title: 'Data stays put', detail: 'Only totals ever leave your database.' },
  { title: 'Honest about our stage', detail: 'Sidwell is early. We say so.' },
];

const TEAM_AREAS = [
  { title: 'Questions to sums', detail: 'Turning what you ask into a calculation that is exactly right.' },
  { title: 'Finance know-how', detail: 'Built around how finance teams think.' },
  { title: 'Product & design', detail: 'Clear enough for real work, not just demos.' },
  { title: 'Security', detail: 'Separation and encryption from day one.' },
];

function About() {
  useSeo({
    title: 'About',
    path: '/about',
    description: 'Sidwell started with one question: why trust a number without seeing where it came from? An early-stage tool that shows the working behind every figure.',
  });

  return (
    <main className="relative w-full">
      <section className="relative bg-limestone">
        <Mullions />
        <div className="shell">
          <Reveal className="grid-12 relative gap-y-6 pb-10 pt-14 lg:pb-14 lg:pt-24">
            <div className="col-span-4 lg:col-span-8">
              <Label className="mb-5">About Sidwell</Label>
              <h1 className="type-h1">Tired of trusting dashboards.</h1>
            </div>
            <div className="col-span-4 self-end lg:col-span-4">
              <p className="type-lead text-glass">Why trust a number without seeing where it came from? So we built a tool that shows you.</p>
              <p className="mt-5 border-t border-wall/25 pt-3 text-[14px] font-semibold">Early stage</p>
            </div>
          </Reveal>
        </div>
        <ArchImage name="atrium" priority sizes="100vw" className="relative z-10 -mb-16 aspect-[16/9] w-full lg:-mb-24 lg:aspect-[21/8]" />
      </section>

      <Section tone="white" pad="none" className="pb-[clamp(72px,10vw,152px)] pt-[calc(clamp(72px,10vw,152px)+64px)] lg:pt-[calc(clamp(72px,10vw,152px)+96px)]">
        <div className="shell">
          <SectionHead
            layout="offset"
            label="Why we exist"
            title="Every number should defend itself."
            aside="Teams have too many dashboards and no way to ask follow-ups. When a chart looks wrong, nobody can check it. Sidwell fixes that."
          />
          <dl className="m-0 mt-12 grid grid-cols-3 border-y border-wall/25 lg:mt-16">
            {FACTS.map((fact) => (
              <div key={fact.label} className="border-l border-wall/25 px-3 py-6 first:border-l-0 first:pl-0 sm:px-7 lg:py-9">
                <dd className="m-0 font-display text-[clamp(32px,5vw,76px)] font-light leading-none figure">{fact.value}</dd>
                <dt className="label mt-3 text-glass">{fact.label}</dt>
              </div>
            ))}
          </dl>
        </div>
      </Section>

      <Section tone="limestone">
        <div className="shell">
          <div className="grid-12 gap-y-10">
            <ArchImage name="colonnade" sizes="(min-width: 1024px) 34vw, 100vw" className="col-span-4 aspect-[4/3] lg:col-span-4 lg:aspect-auto" />
            <div className="col-span-4 lg:col-span-7 lg:col-start-6">
              <Label className="mb-5">How we build</Label>
              <h2 className="type-h2">Four rules we keep.</h2>
              <p className="type-body mt-5 text-glass">Exact numbers, visible working, data that stays put, honesty about our stage.</p>
              <div className="mt-10">
                <RuledSpread items={PRINCIPLES} cols={2} notes />
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section tone="stone">
        <div className="shell">
          <Rail
            label="The story"
            title="It started small."
            aside="Version one knew two things: transactions and budgets. It never guessed. Keeping that promise on any data became the real product."
          >
            <RuledRows items={STORY_STEPS} />
          </Rail>
        </div>
      </Section>

      <Section tone="wall">
        <div className="shell">
          <SectionHead dark label="The team" title="Small team. Focused." aside="Four areas. The same people own the engine and what it says." />
          <div className="mt-12 lg:mt-16">
            <RuledSpread items={TEAM_AREAS} cols={4} dark />
          </div>
        </div>
      </Section>

      <CtaSection
        image="boardroom"
        eyebrow="Talk to Us"
        title="Meet the team."
        subtitle="A live walkthrough beats a pitch deck."
        primary={{ label: 'Request a demo', to: '/#request-briefing' }}
        secondary={{ label: 'How it’s built', to: '/innovation' }}
      />
    </main>
  );
}

export default About;
